import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaFolderOpen, FaSyncAlt } from 'react-icons/fa';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PaginationBar from '@/components/PaginationBar';
import SortHeader, { type SortDirection } from '@/components/SortHeader';
import ScanStateBadge from './ScanStateBadge';
import InventoryNav from './InventoryNav';
import { formatBytes, formatCount, formatDate } from './format';
import {
  getAgeBuckets,
  getExtensionSummary,
  getInventorySites,
  getInventorySummary,
  type AgeBucketDto,
  type ExtensionSummaryDto,
  type InventorySummaryDto,
  type PagedResultDto,
  type SiteInventoryDto,
  type SiteScope,
} from '@/api/sharepointInventoryApi';

const PAGE_SIZE = 25;

const Placeholder: React.FC = () => <span className="placeholder-glow"><span className="placeholder col-6" /></span>;

const ChartLoading: React.FC = () => (
  <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted gap-2">
    <Spinner animation="border" />
    <div className="small">Aggregating file data&hellip;</div>
  </div>
);

const SiteCoveragePage: React.FC = () => {
  const [summary, setSummary] = useState<InventorySummaryDto | null>(null);
  const [extensions, setExtensions] = useState<ExtensionSummaryDto[]>([]);
  const [ageBuckets, setAgeBuckets] = useState<AgeBucketDto[]>([]);
  const [sites, setSites] = useState<PagedResultDto<SiteInventoryDto> | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [siteScope, setSiteScope] = useState<SiteScope>('SharePoint');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [scanState, setScanState] = useState('');
  const [sortBy, setSortBy] = useState('totalBytes');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    (async () => {
      try {
        const [s, e, a] = await Promise.all([
          getInventorySummary(siteScope),
          getExtensionSummary(siteScope, undefined, 15),
          getAgeBuckets(siteScope),
        ]);
        if (cancelled) return;
        setSummary(s);
        setExtensions(e);
        setAgeBuckets(a);
      } catch {
        if (!cancelled) setError('Failed to load inventory summary.');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, siteScope]);

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInventorySites({
        siteScope,
        search: submittedSearch || undefined,
        scanState: scanState || undefined,
        skip: (page - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
        sortBy,
        sortDirection,
      });
      setSites(result);
      setError(null);
    } catch {
      setError('Failed to load sites.');
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, scanState, page, sortBy, sortDirection, siteScope]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const onSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const applyFilters = (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmittedSearch(search.trim());
    setPage(1);
  };

  const coverage = summary?.coverage;

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-1">SharePoint Inventory</h3>
          <div className="text-muted small">
            Latest observed file metadata from the file crawl. Covers non-hidden document libraries across all
            webs in each site collection &mdash; lists, site pages and attachments are not included.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Form.Select
            size="sm"
            style={{ maxWidth: 150 }}
            value={siteScope}
            onChange={(e) => {
              setSiteScope(e.target.value as SiteScope);
              setPage(1);
            }}
          >
            <option value="SharePoint">SharePoint only</option>
            <option value="OneDrive">OneDrive only</option>
            <option value="All">SharePoint + OneDrive</option>
          </Form.Select>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setRefreshKey((k) => k + 1);
              loadSites();
            }}
            disabled={summaryLoading}
          >
            <FaSyncAlt className="me-1" />Refresh
          </Button>
          <Link className="btn btn-primary btn-sm" to="/admin/sharepoint/inventory/files">
            <FaFolderOpen className="me-1" />Browse all files
          </Link>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <InventoryNav />

      {summaryLoading && (
        <Alert variant="secondary" className="d-flex align-items-center gap-2 mb-3">
          <Spinner animation="border" size="sm" />
          <span>Calculating tenant totals&hellip; this can take a while on a large inventory.</span>
        </Alert>
      )}

      {coverage && (
        <Alert variant={coverage.sitesNeverScanned > 0 ? 'warning' : 'info'} className="mb-3">
          <div className="d-flex flex-wrap gap-4">
            <div>
              <strong>{formatCount(coverage.sitesScanned)}</strong> of{' '}
              <strong>{formatCount(coverage.eligibleSites)}</strong> eligible sites scanned
            </div>
            <div>Never scanned: <strong>{formatCount(coverage.sitesNeverScanned)}</strong></div>
            <div>Stale (&gt;{coverage.staleAfterDays}d): <strong>{formatCount(coverage.sitesStale)}</strong></div>
            <div>Scanning now: <strong>{formatCount(coverage.sitesRunning)}</strong></div>
            <div>Failed: <strong>{formatCount(coverage.sitesFailed)}</strong></div>
          </div>
          <div className="small mt-2">
            Scans completed between {formatDate(coverage.oldestCompletedDt)} and {formatDate(coverage.newestCompletedDt)}.
            Scope: {siteScope === 'SharePoint' ? 'SharePoint sites only' : siteScope === 'OneDrive' ? 'OneDrive only' : 'SharePoint + OneDrive'}.
            Figures below describe only what has been inventoried so far, not the whole tenant.
          </div>
        </Alert>
      )}

      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Inventoried files</div>
            <div className="fs-4">{summaryLoading ? <Placeholder /> : formatCount(summary?.inventoriedFileCount)}</div>
            <Link className="small" to="/admin/sharepoint/inventory/files">Browse and filter all files</Link>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Inventoried current-file storage</div>
            <div className="fs-4">{summaryLoading ? <Placeholder /> : formatBytes(summary?.inventoriedBytes)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Not modified in 5+ years</div>
            <div className="fs-4">{summaryLoading ? <Placeholder /> : formatCount(summary?.notModifiedSince5yCount)}</div>
            <div className="text-muted small">{summaryLoading ? '' : formatBytes(summary?.notModifiedSince5yBytes)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Not modified in 7+ years</div>
            <div className="fs-4">{summaryLoading ? <Placeholder /> : formatCount(summary?.notModifiedSince7yCount)}</div>
            <div className="text-muted small">{summaryLoading ? '' : formatBytes(summary?.notModifiedSince7yBytes)}</div>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={7}>
          <Card>
            <Card.Header>Largest extensions by storage</Card.Header>
            <Card.Body style={{ height: 320 }}>
              {summaryLoading ? (
                <ChartLoading />
              ) : extensions.length === 0 ? (
                <div className="text-muted">No file data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={extensions.map((e) => ({
                      name: e.fileExtension || '(none)',
                      bytes: e.totalBytes,
                      files: e.fileCount,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => formatBytes(Number(v))} width={80} />
                    <Tooltip
                      formatter={(value, name) =>
                        name === 'Storage' ? formatBytes(Number(value)) : formatCount(Number(value))
                      }
                    />
                    <Bar dataKey="bytes" fill="#0d6efd" name="Storage" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card>
            <Card.Header>Time since last modified</Card.Header>
            <Card.Body style={{ height: 320 }}>
              {summaryLoading ? (
                <ChartLoading />
              ) : ageBuckets.length === 0 ? (
                <div className="text-muted">No file data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ageBuckets.map((b) => ({ name: b.bucket, bytes: b.totalBytes }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => formatBytes(Number(v))} width={80} />
                    <Tooltip formatter={(value) => formatBytes(Number(value))} />
                    <Bar dataKey="bytes" name="Storage">
                      {ageBuckets.map((b) => (
                        <Cell key={b.bucket} fill={b.sortOrder >= 4 && b.sortOrder <= 6 ? '#fd7e14' : '#0d6efd'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>Sites</Card.Header>
        <Card.Body>
          <Form onSubmit={applyFilters} className="d-flex flex-wrap gap-2 mb-3">
            <Form.Control
              style={{ maxWidth: 320 }}
              placeholder="Search title or URL"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Form.Select
              style={{ maxWidth: 200 }}
              value={scanState}
              onChange={(e) => {
                setScanState(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All scan states</option>
              <option value="Current">Scanned</option>
              <option value="Stale">Stale</option>
              <option value="Running">Scanning</option>
              <option value="Failed">Failed</option>
              <option value="NeverScanned">Never scanned</option>
            </Form.Select>
            <button className="btn btn-primary" type="submit">Apply</button>
          </Form>

          {loading && !sites ? (
            <Spinner animation="border" />
          ) : (
            <>
              <Table hover size="sm" responsive>
                <thead>
                  <tr>
                    <th><SortHeader label="Site" column="title" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th>State</th>
                    <th className="text-end"><SortHeader label="Files" column="fileCount" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th className="text-end"><SortHeader label="Storage" column="totalBytes" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Last completed scan" column="lastCompleted" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Last attempt" column="lastAttempt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                  </tr>
                </thead>
                <tbody>
                  {sites?.items.map((s) => (
                    <tr key={s.siteCollectionId}>
                      <td>
                        <Link to={`/admin/sharepoint/inventory/sites/${s.siteCollectionId}`}>{s.title || s.url}</Link>
                        <div className="text-muted small">{s.url}</div>
                      </td>
                      <td><ScanStateBadge state={s.scanState} /></td>
                      <td className="text-end">{formatCount(s.fileCount)}</td>
                      <td className="text-end">{formatBytes(s.totalBytes)}</td>
                      <td>
                        {formatDate(s.lastCompletedCrawlDt)}
                        {s.crawlAgeDays !== undefined && s.crawlAgeDays !== null && (
                          <div className="text-muted small">{s.crawlAgeDays} days ago</div>
                        )}
                      </td>
                      <td>
                        {formatDate(s.lastAttemptCrawlDt)}
                        {s.lastAttemptStatus && <div className="text-muted small">{s.lastAttemptStatus}</div>}
                      </td>
                    </tr>
                  ))}
                  {sites?.items.length === 0 && (
                    <tr><td colSpan={6} className="text-muted">No sites match these filters.</td></tr>
                  )}
                </tbody>
              </Table>
              {sites && (
                <PaginationBar page={page} pageSize={PAGE_SIZE} total={sites.totalCount} onChange={setPage} />
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SiteCoveragePage;
